import { useDispatch, useSelector } from "react-redux";
import {
  Appstore,
  setAdvancedSearchIsOpen,
  toggleAdvancedSearchIsOpen,
} from "../../../redux";
import RadioButton from "../../Atomos/Radio/RadioButton";
import Input from "../../Atomos/Inputs/Input";
import Dropdown from "../../Atomos/DropDown/Dropdown";
import NewButton from "../../Atomos/Buttons/newButton";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import { MembershipStatus } from "../../../Enums/FirmEnum";
import { getAllFirms } from "../../../Services/Firm";
import IFirm from "../../../interfaces/models/IFirm";
import { isExcludedStaffOrDemoFirm } from "../../../utils/wsgStaffFirms";
import {
  firmLongName,
  firmMemberSearchLabel,
} from "../../../utils/firmDisplayName";

interface IRegion {
  id: string;
  name: string;
}

type LegacyMemberFirmOption = Pick<
  IFirm,
  "id" | "name" | "legacyId" | "shortId"
>;

interface ISearchObj {
  searchType: number;
  keyword?: string;
  lastName?: string;
  firm?: LegacyMemberFirmOption;
  region?: IRegion;
}

const LEGACY_REGIONS: IRegion[] = [
  { id: "1", name: "Africa" },
  { id: "2", name: "Asia Pacific" },
  { id: "3", name: "Caribbean" },
  { id: "4", name: "Central America" },
  { id: "5", name: "Europe" },
  { id: "6", name: "Middle East" },
  { id: "7", name: "North America" },
  { id: "8", name: "South America" },
  { id: "9", name: "Latin America" },
];

const MemberSearchLegacy = () => {
  const { advancedSearchIsOpen } = useSelector(
    (state: Appstore) => state.menus,
  );
  const dispatch = useDispatch();
  const [searchObj, setSearchObj] = useState<ISearchObj>({ searchType: 0 });
  const [memberFirms, setMemberFirms] = useState<LegacyMemberFirmOption[]>([]);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    getAllFirms()
      .then((firms) => {
        if (cancelled) {
          return;
        }

        const options = firms
          .filter(
            (firm) =>
              firm.membershipStatus === MembershipStatus.Member &&
              !isExcludedStaffOrDemoFirm({
                firmId: firm.id,
                legacyId:
                  firm.legacyId ??
                  (firm.shortId != null ? String(firm.shortId) : undefined),
                firmName: firmLongName(firm),
              }),
          )
          .map((firm) => ({
            id: firm.id,
            name: firmMemberSearchLabel(firm),
            legacyId: firm.legacyId,
            shortId: firm.shortId,
          }))
          .filter((firm) => firm.name.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name));

        setMemberFirms(options);
      })
      .catch((error) => {
        console.error("Failed to load firms for legacy member search:", error);
        if (!cancelled) {
          setMemberFirms([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearchObj = (field: keyof ISearchObj, value: unknown) => {
    setSearchObj((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    dispatch(setAdvancedSearchIsOpen(false));
  }, [pathname, dispatch]);

  const buildSearchUrl = () => {
    const baseUrl = "/search-special.asp?from=advanced&top=1&action=search";
    const params = new URLSearchParams();

    params.append("p40", searchObj.searchType === 1 ? "pro" : "firm");
    if (searchObj.keyword) {
      params.append("words", searchObj.keyword);
    }
    if (searchObj.lastName) {
      params.append("p12", searchObj.lastName);
    }
    const firmP14 = searchObj.firm?.legacyId ?? searchObj.firm?.shortId;
    if (
      firmP14 !== undefined &&
      firmP14 !== null &&
      String(firmP14).trim() !== ""
    ) {
      params.append("p14", String(firmP14).trim());
    }
    if (searchObj.region?.id) {
      params.append("region", searchObj.region.id);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}&${queryString}` : baseUrl;
  };

  const handleSearch = () => {
    navigate(buildSearchUrl());
    dispatch(setAdvancedSearchIsOpen(false));
  };

  const selectedFirmLabel = useMemo(() => {
    if (!searchObj.firm?.id) {
      return searchObj.firm?.name;
    }
    return (
      memberFirms.find((firm) => firm.id === searchObj.firm?.id)?.name ??
      searchObj.firm?.name
    );
  }, [memberFirms, searchObj.firm]);

  return (
    <div
      className={`${
        advancedSearchIsOpen
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-5 opacity-0"
      } absolute right-1 top-6 z-50 flex w-80 max-w-sm transform rounded-md bg-gray-100 shadow-md shadow-black/50 transition-all duration-300 ease-in-out md:right-0 md:z-40 md:w-full md:max-w-none`}
    >
      <div className="relative flex w-full flex-wrap items-center gap-4 px-4 py-4 lg:flex-nowrap">
        <div className="block font-decimal text-2xl font-normal text-neutral-dark lg:hidden">
          Member Search
        </div>
        <div className="flex flex-1 justify-start gap-4 pr-6 text-gray-font md:justify-end lg:justify-start lg:pr-0">
          <RadioButton
            index={0}
            text="Firm"
            selected={searchObj.searchType === 0}
            handleRadioButtonChange={() => handleSearchObj("searchType", 0)}
            fontSize="!text-base md:!text-lg "
            radioSize="w-4 h-4 p-[.15rem]"
            color="secondary"
          />
          <RadioButton
            index={1}
            text="Professionals"
            selected={searchObj.searchType === 1}
            handleRadioButtonChange={() => handleSearchObj("searchType", 1)}
            fontSize="!text-base md:!text-lg "
            radioSize="w-4 h-4 p-[.15rem]"
          />
        </div>

        <div className="h-4 w-[2px] bg-black" />
        <div className="flex w-full flex-auto flex-col items-center gap-1 lg:flex-row">
          <Input
            title=""
            name="keyword"
            placeholder="Keyword Search"
            value={searchObj.keyword}
            onChange={(e) => handleSearchObj("keyword", e.target.value)}
            error={false}
            className="!border-[#E8E8E8] !bg-[#E8E8E8]"
          />
          {searchObj.searchType === 1 ? (
            <>
              <div className="h-4 w-[3px] bg-black" />
              <Input
                title=""
                name="lastName"
                placeholder="Last Name"
                value={searchObj.lastName}
                onChange={(e) => handleSearchObj("lastName", e.target.value)}
                error={false}
                className="!border-[#E8E8E8] !bg-[#E8E8E8]"
              />
            </>
          ) : null}
          <div className="h-4 w-[3px] bg-black" />
          <Dropdown
            title=""
            data={memberFirms}
            selectedObjst={selectedFirmLabel}
            selectedValue={searchObj.firm}
            seter={(firm) => handleSearchObj("firm", firm)}
            compareBy="id"
            getid
            placeholder="Select Firm"
            maxHeight="max-h-120"
            ddClass="!border-[#E8E8E8] !bg-[#E8E8E8]"
            internalSearch
          />
          <div className="h-4 w-[3px] bg-black" />
          {searchObj.searchType === 0 ? (
            <Dropdown
              title=""
              data={LEGACY_REGIONS}
              selectedObjst={searchObj.region?.name}
              seter={(e) => handleSearchObj("region", e)}
              getid
              placeholder="Select Region"
              maxHeight="max-h-120"
              ddClass="!border-[#E8E8E8] !bg-[#E8E8E8]"
              internalSearch
            />
          ) : null}
        </div>

        <div className="flex flex-auto flex-col justify-end gap-3 md:flex-row">
          <NewButton
            onClick={handleSearch}
            text="Search"
            content="textIcon"
            color="accent"
            size="large"
            className="w-full md:max-w-[120px]"
          />
          <NewButton
            onClick={() => navigate("/advanced-search.asp")}
            text="Advanced Search"
            content="textIcon"
            color="accent"
            size="large"
            className="w-full md:max-w-[15.25rem] lg:min-w-[250px]"
          />
        </div>
      </div>

      <div className="absolute right-0 top-0 mr-2 mt-2 flex items-start justify-end lg:relative">
        <button
          type="button"
          className="h-6 w-6 rounded-full bg-secondary p-1 text-white"
          onClick={() => dispatch(toggleAdvancedSearchIsOpen())}
        >
          <CloseModal />
        </button>
      </div>
    </div>
  );
};

export default MemberSearchLegacy;
